import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, Button, Chip, Box, Alert, CircularProgress, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tooltip, IconButton
} from '@mui/material';
import { Send, RefreshCw, X, Trash2 } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

type InvitationStatus = 'pending' | 'accepted' | 'profile_created' | 'bands_inherited' | 'revoked';

interface Invitation {
  _id: string;
  email: string;
  name?: string;
  status: InvitationStatus;
  invitedAt: string;
  expiresAt: string;
  acceptedAt?: string;
  matchedBands: string[];
  invitedBy: { name: string; email: string } | null;
  userId: { name: string; email: string } | null;
}

const STATUS_COLORS: Record<InvitationStatus, 'default' | 'success' | 'info' | 'primary' | 'error' | 'warning'> = {
  pending: 'default',
  accepted: 'success',
  profile_created: 'info',
  bands_inherited: 'primary',
  revoked: 'error'
};

const STATUS_LABELS: Record<InvitationStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  profile_created: 'Profile Created',
  bands_inherited: 'Bands Inherited',
  revoked: 'Revoked'
};

const ArtistInvitationsPage: React.FC = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const response = await axiosInstance.get<Invitation[]>('/api/artist-invitations');
        setInvitations(response.data);
      } catch {
        setError('Failed to load invitations');
      } finally {
        setLoading(false);
      }
    };
    fetchInvitations();
  }, []);

  const handleInvite = async () => {
    setInviteError(null);
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }
    setInviting(true);
    try {
      const response = await axiosInstance.post<Invitation>('/api/artist-invitations', {
        email: inviteEmail.trim(),
        name: inviteName.trim() || undefined
      });
      setInvitations(prev => [response.data, ...prev]);
      setDialogOpen(false);
      setInviteEmail('');
      setInviteName('');
      showSuccess(`Invitation sent to ${response.data.email}`);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to send invitation';
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleResend = async (inv: Invitation) => {
    setActionId(inv._id);
    try {
      const response = await axiosInstance.post<Invitation>(`/api/artist-invitations/${inv._id}/resend`);
      setInvitations(prev => prev.map(i => i._id === inv._id ? { ...i, ...response.data } : i));
      showSuccess(`Invitation resent to ${inv.email}`);
    } catch {
      setError('Failed to resend invitation');
    } finally {
      setActionId(null);
    }
  };

  const handleRevoke = async (inv: Invitation) => {
    setActionId(inv._id);
    try {
      const response = await axiosInstance.delete<Invitation>(`/api/artist-invitations/${inv._id}`);
      setInvitations(prev => prev.map(i => i._id === inv._id ? { ...i, status: response.data.status } : i));
      showSuccess(`Invitation revoked for ${inv.email}`);
    } catch {
      setError('Failed to revoke invitation');
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async (inv: Invitation) => {
    setActionId(inv._id);
    try {
      await axiosInstance.delete(`/api/artist-invitations/${inv._id}/permanent`);
      setInvitations(prev => prev.filter(i => i._id !== inv._id));
      showSuccess(`Invitation for ${inv.email} permanently deleted`);
    } catch {
      setError('Failed to delete invitation');
    } finally {
      setActionId(null);
    }
  };

  const isExpired = (inv: Invitation) =>
    inv.status === 'pending' && new Date() > new Date(inv.expiresAt);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Artist Invitations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Invite artists by email. Invitations expire after 30 days. Artists inherit matching bands on first login.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Send size={16} />}
          onClick={() => setDialogOpen(true)}
          sx={{ mt: 0.5, flexShrink: 0 }}
        >
          Invite Artist
        </Button>
      </Box>

      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : invitations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No invitations sent yet.</Typography>
        </Paper>
      ) : (
        <Table component={Paper}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Matched Bands</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map(inv => {
              const expired = isExpired(inv);
              const isActive = actionId === inv._id;
              const canResend = inv.status !== 'revoked';
              const canRevoke = inv.status !== 'revoked' && inv.status !== 'bands_inherited';
              const canDelete = inv.status === 'revoked';

              return (
                <TableRow key={inv._id}>
                  <TableCell>{inv.name || inv.userId?.name || '—'}</TableCell>
                  <TableCell>{inv.email}</TableCell>
                  <TableCell>{new Date(inv.invitedAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Chip
                        label={expired ? 'Expired' : STATUS_LABELS[inv.status]}
                        size="small"
                        color={expired ? 'warning' : STATUS_COLORS[inv.status]}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(inv.matchedBands ?? []).length > 0 ? (
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {(inv.matchedBands ?? []).map((name, i) => (
                          <Chip key={i} label={name} size="small" color="primary" variant="outlined" />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      {canResend && (
                        <Tooltip title="Resend invitation">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleResend(inv)}
                              disabled={isActive}
                            >
                              <RefreshCw size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canRevoke && (
                        <Tooltip title="Revoke invitation">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRevoke(inv)}
                              disabled={isActive}
                            >
                              <X size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete permanently">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(inv)}
                              disabled={isActive}
                              color="error"
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setInviteError(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Invite an Artist</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {inviteError && <Alert severity="error">{inviteError}</Alert>}
            <TextField
              label="Email address"
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Name (optional)"
              value={inviteName}
              onChange={e => setInviteName(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setInviteError(null); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={inviting}
            startIcon={<Send size={16} />}
          >
            {inviting ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ArtistInvitationsPage;
